import { Box, Pagination, Stack, Tab, TextField } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcIconButtonDropdown, MprcRequestErrorReport } from 'components';
import { MprcFilterBox, MprcFilterCount, MprcFilterLine } from 'components/filter';
import { IconSort } from "components/icons";
import { MprcChipTabs } from 'components/MprcChipTabs';
import { MprcRibbonListCardsExpandedContainer } from 'components/MprcRibbonList';
import { MprcRibbonListCardsGridContainer } from 'components/MprcRibbonList/MprcRibbonListCardContainers';
import { CardDtoPaginatedList, CarouselCardRenderMode, LibraryTrainingsOption, LibraryTrainingsOrderBy } from "generated/CoreApi_v1";
import { useSimpleDebounce } from 'hooks/useDebounce';
import TzUtils from 'lib/TzUtils';
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralCardLoading } from 'views/general/Cards';
import { CardDtoToCardRibbon } from 'views/general/Cards/CardDtoToCardRibbon';

interface StudentLibraryFilter {
    option: LibraryTrainingsOption;
    orderBy: LibraryTrainingsOrderBy;
}

const getClearFilter = (): StudentLibraryFilter => (
    {
        orderBy: LibraryTrainingsOrderBy.MostRecent,
        option: LibraryTrainingsOption.Bookmarked,
    }
);

export const StudentLibrary: React.FC = () => {
    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const [loading, setLoading] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [viewFilter, setViewFilter] = useState<StudentLibraryFilter>(getClearFilter());
    const [viewData, setViewData] = useState<CardDtoPaginatedList | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const searchTextDebounce = useSimpleDebounce(searchText);

    const filtered = useMemo(
        () => searchText.length > 0
        , [searchText]
    );

    ///
    async function loadData() {
        setLoading(true);

        try {

            const result = await api.v1.lmsStudent.getLibrary({
                ...viewFilter,
                browserTimeZoneId: TzUtils.browserTimezone(),
                searchText,
                pageNumber,
                pageSize: APP_PAGE_SIZE_DEFAULT
            });

            setViewData(result.data);
        }
        catch (e) {
            setViewData(null);
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const resetFilters = () => {
        setSearchText("");
        setViewFilter(getClearFilter());
    };

    const handleFilter = (data: Partial<StudentLibraryFilter>) => {
        setViewFilter(state => ({ ...state, ...data }));
        setPageNumber(1);
    };

    useEffect(() => {
        loadData();
    }, [viewFilter, pageNumber, searchTextDebounce]);

    const RenderFilter = (
        <MprcFilterBox>
            <MprcFilterLine>
                <div>
                    <MprcChipTabs
                        value={viewFilter.option}
                        onChange={(e, value) => handleFilter({ option: value })}
                    >
                        <Tab
                            label={t("Student.Library.SavedOption")}
                            value={LibraryTrainingsOption.Bookmarked}
                        />
                        <Tab
                            label={t("Student.Library.LikedOption")}
                            value={LibraryTrainingsOption.Liked}
                        />
                    </MprcChipTabs>
                </div>
                <div>
                    <MprcIconButtonDropdown
                        menuItemCheck
                        icon={<IconSort />}
                        selected={viewFilter.orderBy}
                        onChange={(orderBy) => handleFilter({ orderBy: orderBy })}
                        items={[
                            { text: t("Student.Library.OrderBy.MostRecent"), value: LibraryTrainingsOrderBy.MostRecent },
                            { text: t("Student.Library.OrderBy.Title"), value: LibraryTrainingsOrderBy.Title },
                        ]}
                        title={t("Filter.OrderBy", { ns: "shared" })}
                    />
                </div>
            </MprcFilterLine>
            <TextField
                fullWidth
                value={searchText}
                autoFocus
                autoComplete="off"
                placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                onChange={(e) => setSearchText(e.target.value)}
            />
        </MprcFilterBox>
    );

    ///
    const RenderContent = (
        <>

            <Box sx={{ m: 2 }}>
                <MprcFilterCount
                    loading={loading}
                    isEmpty={(viewData?.totalRows ?? 0) == 0}
                    countText={t("Student.Catalog.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                    filter={{
                        active: filtered,
                        text: t("Student.Catalog.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                        onResetFilter: resetFilters
                    }}
                />
            </Box>

            <MprcRibbonListCardsGridContainer>
                {
                    loading
                        ? <GeneralCardLoading />
                        : CardDtoToCardRibbon(viewData?.items, CarouselCardRenderMode.Default)
                }
            </MprcRibbonListCardsGridContainer>

            {
                viewData && viewData.totalPages > 1 &&
                <Stack justifyItems="center">
                    <Pagination
                        shape="rounded"
                        color="primary"
                        page={viewData.pageNumber}
                        count={viewData.totalPages}
                        onChange={(e, page) => setPageNumber(page)}
                    />
                </Stack>
            }

        </>
    );

    return (
        viewError
            ? <MprcRequestErrorReport error={viewError} />
            : (
                <MprcRibbonListCardsExpandedContainer>
                    {RenderFilter}
                    {RenderContent}
                </MprcRibbonListCardsExpandedContainer>
            )
    );
};
